import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { MapPin, Clock, Phone, Mail, ShieldCheck, Navigation, CalendarCheck } from 'lucide-react';

interface VisitAndHoursSectionProps {
  onOpenOrderApp: () => void;
  onOpenLegalModal: (type: 'allergen' | 'terms' | 'privacy') => void;
}

export const VisitAndHoursSection: React.FC<VisitAndHoursSectionProps> = ({
  onOpenOrderApp,
  onOpenLegalModal
}) => {
  const { lang } = useLanguage();

  const businessInfo = {
    name: 'The Oven Story',
    domain: 'theovenstory.fi',
    address: 'Aleksanterinkatu 18, 00170 Helsinki, Finland',
    businessId: 'FI3149821-4',
    phone: '+358 40 555 8920',
    email: 'asiakaspalvelu@theovenstory.fi',
    hours: [
      { day: { fi: 'Maanantai – Torstai', en: 'Monday – Thursday', sv: 'Måndag – Torsdag' }, time: '11:00 – 22:00' },
      { day: { fi: 'Perjantai', en: 'Friday', sv: 'Fredag' }, time: '11:00 – 23:00' },
      { day: { fi: 'Lauantai', en: 'Saturday', sv: 'Lördag' }, time: '12:00 – 23:00' },
      { day: { fi: 'Sunnuntai', en: 'Sunday', sv: 'Söndag' }, time: '12:00 – 21:00' }
    ]
  };

  const text = {
    eyebrow: {
      fi: 'SIJAINTI & AUKIOLOAJAT',
      en: 'LOCATION & OPENING HOURS',
      sv: 'LÄGE & ÖPPETTIDER'
    },
    title: {
      fi: 'Tervetuloa Aleksanterinkadulle',
      en: 'Welcome to Aleksanterinkatu 18',
      sv: 'Välkommen till Alexandersgatan 18'
    },
    desc: {
      fi: 'Löydät meidät aivan Helsingin ytimestä. Tule lounaalle, iltapäiväkahveille, illalliselle tai rentoutumaan cocktailin ääreen.',
      en: 'Find us in the vibrant center of Helsinki. Join us for lunch, dinner, after-work craft beers, or late-night artisan cocktails.',
      sv: 'Du hittar oss mitt i Helsingfors centrum. Välkommen på lunch, middag eller kvällsdrinkar.'
    },
    btnBook: {
      fi: 'Varaa Pöytä Nyt →',
      en: 'Book Your Table Now →',
      sv: 'Boka Ditt Bord Nu →'
    },
    btnMaps: {
      fi: 'Reittiohjeet (Google Maps) ↗',
      en: 'Directions (Google Maps) ↗',
      sv: 'Vägbeskrivning ↗'
    },
    oivaBadge: {
      fi: 'Allergeenit & Elintarviketurvallisuus (Oiva-raportti)',
      en: 'Allergens & Food Safety Verified (Oiva Report)',
      sv: 'Allergener & Livsmedelssäkerhet (Oiva-rapport)'
    }
  };

  return (
    <section id="visit" className="os-page-section">
      <div className="os-container">
        <div className="os-section-header">
          <span className="os-eyebrow">{text.eyebrow[lang]}</span>
          <h2 className="os-section-title">{text.title[lang]}</h2>
          <p className="os-section-desc">{text.desc[lang]}</p>
        </div>

        <div className="os-visit-grid">
          {/* Card 1: Hours, Contact & Safety */}
          <div className="os-hours-card">
            <h3 className="os-font-serif" style={{ fontSize: '1.8rem', color: '#ffffff', margin: '0 0 4px 0' }}>
              {businessInfo.name}
            </h3>
            <p style={{ color: 'var(--os-copper)', fontSize: '0.92rem', fontWeight: 600, margin: '0 0 16px 0' }}>
              {businessInfo.address}
            </p>

            {/* Opening Hours Table */}
            <ul className="os-hours-list">
              {businessInfo.hours.map((h, idx) => (
                <li key={idx} className="os-hours-item">
                  <span className="day">{h.day[lang]}</span>
                  <span className="time">{h.time}</span>
                </li>
              ))}
            </ul>

            {/* Phone & Email */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
              <a
                href={`tel:${businessInfo.phone}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--os-text-primary)', textDecoration: 'none', fontSize: '0.95rem' }}
              >
                <Phone size={16} color="var(--os-amber)" />
                <span>Puh: {businessInfo.phone}</span>
              </a>
              <a
                href={`mailto:${businessInfo.email}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--os-text-primary)', textDecoration: 'none', fontSize: '0.95rem' }}
              >
                <Mail size={16} color="var(--os-amber)" />
                <span>Email: {businessInfo.email}</span>
              </a>
            </div>

            {/* Oiva & Food Safety Badge */}
            <div
              onClick={() => onOpenLegalModal('allergen')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(16,185,129,0.1)',
                border: '1px solid rgba(16,185,129,0.3)',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#10b981',
                cursor: 'pointer',
                marginBottom: '24px'
              }}
              title="Avaa allergeeni- ja elintarviketurvallisuustiedot"
            >
              <ShieldCheck size={16} />
              <span>{text.oivaBadge[lang]}</span>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <button onClick={onOpenOrderApp} className="os-btn-book-table" style={{ flex: 1, justifyContent: 'center' }}>
                <CalendarCheck size={16} />
                <span>{text.btnBook[lang]}</span>
              </button>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Aleksanterinkatu+18%2C+Helsinki"
                target="_blank"
                rel="noopener noreferrer"
                className="os-btn-hero-secondary"
                style={{ flex: 1, justifyContent: 'center' }}
              >
                <Navigation size={16} />
                <span>{text.btnMaps[lang]}</span>
              </a>
            </div>
          </div>

          {/* Card 2: Interactive Location Map */}
          <div style={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid var(--os-border)', minHeight: '380px' }}>
            <iframe
              title="The Oven Story Helsinki Map"
              src="https://maps.google.com/maps?q=Aleksanterinkatu%2018%2C%20Helsinki&t=&z=15&ie=UTF8&iwloc=&output=embed"
              style={{ width: '100%', height: '100%', minHeight: '380px', border: 'none', filter: 'grayscale(0.7) invert(0.9) contrast(1.15)' }}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
